import React, { useState } from "react";
import { toast } from "sonner";
import { Power, PowerOff, Loader2, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useSiteStatus } from "../../contexts/SiteStatusContext";

/**
 * Big red kill switch for the admin: toggle the whole public site into
 * "under construction" mode. The admin keeps full access while it's paused.
 */
export default function AdminSitePauseToggle() {
  const { paused, setPaused } = useSiteStatus();
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const perform = async (nextPaused) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/admin/site", { paused: nextPaused });
      setPaused(!!data.paused);
      toast.success(
        data.paused
          ? "Site paused — everyone else now sees ‘Under construction’."
          : "Site is live again."
      );
      setConfirmOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update site status.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-site-pause">
      <div className="flex items-center gap-2 mb-2">
        {paused ? (
          <PowerOff className="w-4 h-4 text-[#ff9c85]" />
        ) : (
          <Power className="w-4 h-4 text-[#2ADB80]" />
        )}
        <h2 className={`label-eyebrow ${paused ? "text-[#ff9c85]" : "text-[#2ADB80]"}`}>
          Admin — Site status
        </h2>
      </div>
      <p className="text-[13px] text-white/60 mb-5 max-w-lg">
        One-click kill-switch for the public site. While it's paused, everyone except you
        sees a <em>wangram — under construction</em> splash. You keep full access to the app and this toggle.
      </p>

      <div
        className={`p-4 rounded-md border transition-colors flex flex-wrap items-center gap-4 ${
          paused
            ? "border-[#ff9c85]/40 bg-[#ff9c85]/[0.06]"
            : "border-[#2ADB80]/30 bg-[#2ADB80]/[0.04]"
        }`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              paused ? "bg-[#ff9c85]/15" : "bg-[#2ADB80]/15"
            }`}
          >
            {paused ? (
              <PowerOff className="w-5 h-5 text-[#ff9c85]" />
            ) : (
              <Power className="w-5 h-5 text-[#2ADB80]" />
            )}
          </div>
          <div className="min-w-0">
            <div className={`font-display text-lg font-black tracking-tight ${paused ? "text-[#ffb097]" : "text-white"}`}>
              {paused ? "Site paused" : "Site is live"}
            </div>
            <div className="text-[12px] text-white/55 font-mono-p">
              {paused
                ? "Visitors (and non-admin logged-in users) see the ‘under construction’ splash."
                : "Everyone can sign in, search and buy coins as normal."}
            </div>
          </div>
        </div>

        {paused ? (
          <button
            type="button"
            onClick={() => perform(false)}
            disabled={busy}
            className="btn-pill focus-ring !bg-[#2ADB80]/15 !border-[#2ADB80]/40 !text-[#8CFFB4] disabled:opacity-50"
            data-testid="admin-site-resume-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-3.5 h-3.5" />}
            <span>Resume site</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={busy}
            className="btn-pill focus-ring !bg-[#ff9c85]/15 !border-[#ff9c85]/40 !text-[#ffb097] disabled:opacity-50"
            data-testid="admin-site-pause-btn"
          >
            <PowerOff className="w-3.5 h-3.5" />
            <span>Pause the site</span>
          </button>
        )}
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmOpen(false); }}
          data-testid="admin-site-pause-modal"
        >
          <div className="w-full max-w-md rounded-lg border border-[#ff9c85]/30 bg-[#0F0F0F] shadow-2xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-[#ff9c85]" />
              <span className="label-eyebrow text-[#ff9c85]">pause the entire site</span>
            </div>
            <h3 className="font-display text-lg font-black tracking-tight mb-3">
              Everyone else is kicked out in a few seconds
            </h3>
            <p className="text-[13px] text-white/70 leading-relaxed mb-5">
              All visitors and logged-in users (except you) will see the “under construction” splash
              within the next 30 seconds. Running searches and in-flight checkouts keep working, but
              new ones can't start. You can flip this back on anytime.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="btn-ghost focus-ring"
                data-testid="admin-site-pause-cancel"
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => perform(true)}
                disabled={busy}
                className="btn-pill focus-ring !bg-[#ff9c85]/15 !border-[#ff9c85]/40 !text-[#ffb097]"
                data-testid="admin-site-pause-confirm"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PowerOff className="w-3.5 h-3.5" />}
                <span>Pause site now</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
